import * as React from 'react'

import { Box, Button, Checkbox } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'

import type AutomaticRuleForm from 'modules/attendance/components/SettingItemPage/TimeTracking/BreakSettings/AutomaticBreakSettings/AutomaticRules/form'
import { AUTOMATIC_BREAK_SOURCE } from 'modules/attendance/hooks/useFetchBreakSettings'
import { TimeSettingsBreakConfigs } from 'modules/attendance/hooks/useFetchTimeSettingsBreakConfigurations'
import { BreakSettingsFormValues } from 'modules/attendance/lib/forms/BreakSettingForm'
import { useTimeTrackingFeatureAvailability } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'

import AutomaticRules from './AutomaticRules'
import ToggleButtons from './ToggleButtons'

interface Props {
  settingForm: FormikProps<BreakSettingsFormValues>
  timeSettingsBreakConfigurations: TimeSettingsBreakConfigs[]
  automaticRuleForms: AutomaticRuleForm[]
  addAutomaticRule: () => void
  onConfirm: () => void
  attendanceSettingId: string
}

const AutomaticBreakSettings = ({
  settingForm,
  timeSettingsBreakConfigurations,
  automaticRuleForms,
  addAutomaticRule,
  onConfirm,
  attendanceSettingId,
}: Props) => {
  const deleteAutomaticRule = (form: AutomaticRuleForm) => {
    if (form.get('id').value) {
      form.get('_destroy').set(true)
      return
    }

    automaticRuleForms.splice(automaticRuleForms.indexOf(form), 1)
  }
  const [sendNotification, setSendNotification] = React.useState(
    settingForm.values.sendNotification
  )

  const shouldShowSaveButton = () => {
    const isPlannedHours =
      settingForm.dirty &&
      settingForm.values.automaticBreakSource === AUTOMATIC_BREAK_SOURCE.PLANNED_HOURS

    const isRules =
      settingForm.values.automaticBreakSource === AUTOMATIC_BREAK_SOURCE.RULES &&
      automaticRuleForms.some((form) => form.isDirty) &&
      automaticRuleForms.every((form) => form.valid())

    return isPlannedHours || isRules
  }

  const handleRulesToggle = () => {
    settingForm.setFieldValue('automaticBreakSource', AUTOMATIC_BREAK_SOURCE.RULES)

    if (automaticRuleForms.length === 0) {
      addAutomaticRule()
      return
    }
  }

  const hasAutomaticPlannedBreaksFeature = useTimeTrackingFeatureAvailability(
    'time_tracking_automatic_planned_breaks'
  )

  return (
    <Box gap="s16">
      {hasAutomaticPlannedBreaksFeature && (
        <ToggleButtons settingForm={settingForm} handleRulesToggle={handleRulesToggle} />
      )}

      {settingForm.values.automaticBreakSource === AUTOMATIC_BREAK_SOURCE.RULES && (
        <AutomaticRules
          automaticRuleForms={automaticRuleForms}
          addAutomaticRule={addAutomaticRule}
          deleteAutomaticRule={deleteAutomaticRule}
          timeSettingsBreakConfigurations={timeSettingsBreakConfigurations}
          attendanceSettingId={attendanceSettingId}
        />
      )}

      <Checkbox
        checked={sendNotification}
        label={i18n.t('time_tracking.settings.breaks.instance.automatic_breaks.send_notification')}
        onChange={() => {
          setSendNotification(!sendNotification)
          settingForm.setFieldValue('sendNotification', !sendNotification)
        }}
      />

      {shouldShowSaveButton() && (
        <Box flexDirection="row" justifyContent="flexStart">
          <Button type="primary" onClick={onConfirm} iconName="save">
            {i18n.t('common.save_changes')}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default observer(AutomaticBreakSettings)
